import { fetchOfflinePointers } from '../routing/offlineData';
import { readBasemap } from './basemap';

/*
 * The base map under every map in the app (the national map, the camera's
 * mini-map, the hub's pin, missions, the GPS picker).
 *
 * The raster tiles of tile.openstreetmap.org go on at once, as they always
 * have. Then, when the offline base map is published (scripts/basemap) and
 * its archive answers -- from the network or from what this device kept --
 * the vector map replaces them. It reads Israel's PMTiles archive by byte
 * range and keeps every range it reads, so whatever was looked at online
 * is there offline, labels in the app's language, light or dark with the
 * theme. Any failure along the way leaves the raster map: nothing is lost.
 */
export const OSM_RASTER = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';

export function currentLang() {
  const lang = typeof document !== 'undefined' ? document.documentElement.getAttribute('lang') : null;
  return lang && lang.startsWith('he') ? 'he' : 'en';
}

export function currentlyDark() {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'field_dark';
}

/**
 * Add the base map to a Leaflet map; returns the raster layer it starts with.
 * options: attribution, maxZoom (raster), vector (false: the raster only),
 * onVector(layer) when the vector map has replaced it.
 */
export function addBaseLayer(L, map, { attribution = '© OpenStreetMap contributors', maxZoom = 19, vector = true,
  onVector } = {}) {
  const raster = L.tileLayer(OSM_RASTER, { attribution, maxZoom }).addTo(map);
  if (vector && typeof window !== 'undefined') {
    upgrade(map, raster, maxZoom).then((layer) => { if (layer) onVector?.(layer); }).catch(() => {});
  }
  return raster;
}

// The same deepest zoom as the raster it replaces: a layer with none leaves
// the map's max zoom Infinity, which hangs leaflet.markercluster (vector.js).
async function upgrade(map, raster, maxZoom) {
  const pointers = await fetchOfflinePointers();
  const pointerUrl = pointers?.basemap?.pointerUrl;
  if (!pointerUrl) return null;
  const info = await readBasemap(pointerUrl);
  const { vectorLayer } = await import('./vector');
  const layer = await vectorLayer(info, { lang: currentLang(), dark: currentlyDark(), maxZoom });
  // The map may have been closed while the archive was read.
  if (!map || !map.getContainer || !map.getContainer()?.isConnected) return null;
  layer.addTo(map);
  raster.remove();
  return layer;
}
